<template>
  <div>
    <notifications group="foo" position="top right" :max="1" class="top-padding"/>
    <router-view :key="$route.fullPath"></router-view>
  </div>
</template>

<script>
import {mapGetters, mapMutations} from "vuex";
import jwtService from "@/app/services/jwt.service";
import {isRefreshInFlight, refreshSessionToken} from "@/app/services/auth.service";
import {
  clearRetryHold,
  getLastRefreshAttemptAt,
  getRefreshDelayMs,
  getSessionRevision,
  MIN_REFRESH_GAP_MS,
  recordRefreshAttempt,
  setRetryHoldEightMinutes,
} from "@/app/services/tokenRefresh";

export default {
  name: 'App',

  data() {
    return {
      profileRefreshTimer: null,
      sessionRevision: getSessionRevision(),
    };
  },

  mounted() {
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener("session-revision-bump", this.onSessionRevisionBump);
  },

  methods: {
    ...mapMutations(["resetNetworkStatus"]),

    clearProfilePoll() {
      if (this.profileRefreshTimer != null) {
        clearTimeout(this.profileRefreshTimer);
        this.profileRefreshTimer = null;
      }
    },

    scheduleProfileRefresh() {
      this.clearProfilePoll();
      if (!this.isSessionActive) {
        return;
      }

      const accessToken = jwtService.getToken();
      const delay = getRefreshDelayMs(accessToken);
      this.profileRefreshTimer = setTimeout(this.runProfileRefresh, delay);
    },

    async runProfileRefresh() {
      this.profileRefreshTimer = null;

      if (!this.isSessionActive) {
        this.clearProfilePoll();
        return;
      }

      const refreshToken = sessionStorage.getItem("tokenRefresh");
      if (!refreshToken) {
        return;
      }

      if (isRefreshInFlight()) {
        return;
      }

      recordRefreshAttempt();

      try {
        const result = await refreshSessionToken(refreshToken);
        if (result == null) {
          return;
        }

        clearRetryHold();
        this.sessionRevision = getSessionRevision();
        this.scheduleProfileRefresh();
      } catch (error) {
        const status = error.response && error.response.status;
        if (status === 401 || status === 403) {
          this.clearProfilePoll();
          return;
        }

        console.error("Token refresh failed:", error);
        setRetryHoldEightMinutes();
        this.scheduleProfileRefresh();
      }
    },

    onSessionRevisionBump() {
      this.sessionRevision = getSessionRevision();
    },

    onVisibilityChange() {
      if (document.visibilityState !== "visible") {
        return;
      }

      if (!this.isSessionActive) {
        return;
      }

      if (isRefreshInFlight()) {
        return;
      }

      if (this.profileRefreshTimer != null) {
        return;
      }

      const accessToken = jwtService.getToken();
      const delay = getRefreshDelayMs(accessToken);
      if (delay !== 0) {
        return;
      }

      const lastAttempt = getLastRefreshAttemptAt();
      if (lastAttempt != null && Date.now() - lastAttempt < MIN_REFRESH_GAP_MS) {
        return;
      }

      this.runProfileRefresh();
    },
  },

  computed: {
    ...mapGetters(["getNetworkStatus", "getUserProfileData"]),

    isSessionActive() {
      const revision = this.sessionRevision;
      void revision;

      const profile =
        this.getUserProfileData ||
        jwtService.getUser() ||
        JSON.parse(sessionStorage.getItem("current_user") || "null");
      const token =
        jwtService.getToken() ||
        sessionStorage.getItem("access_token");
      return !!(profile && token);
    },
  },

  watch: {
    getNetworkStatus(message) {
      if (message !== null) {
        this.errorToast('Network Error', message);
      }
      this.resetNetworkStatus();
    },

    isSessionActive: {
      immediate: true,
      handler(isActive) {
        this.clearProfilePoll();
        if (!isActive) {
          return;
        }
        this.scheduleProfileRefresh();
      },
    },
  },

  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("session-revision-bump", this.onSessionRevisionBump);
    this.clearProfilePoll();
  },
};
</script>


<style>

*{
  scrollbar-width: thin;
  scrollbar-color: #cdcdcd #f0f0f0;
}

/* Works on Chrome, Edge, and Safari */
*::-webkit-scrollbar {
  width: 8px;
}

*::-webkit-scrollbar-track {
  background: #f0f0f0;
}

*::-webkit-scrollbar-thumb {
  background-color: #cdcdcd;
  /*background: linear-gradient(to top, #33ccff 0%, #cc00ff 100%)*/
}

.top-padding{
  margin-top: 70px;
}

.noFocus:focus {
  outline: none 0 !important;
  box-shadow: none;
  -moz-box-shadow: none;
  -webkit-box-shadow: none;
}

.noFocus {
  box-shadow: none !important
}

</style>
